import { useState } from "react";
import { InPlaceEditor, type InPlaceEditorProps } from "react-admin";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

/**
 * Drop-in replacement for react-admin's `<InPlaceEditor>` that surfaces the
 * backend's full error (title + actionable details + code) on a failed save,
 * instead of RA's generic default `onError` — which notifies the untranslated
 * `ra.notification.http_error` ("Server communication error") and throws away
 * whatever the API actually said (e.g. "Partner is read-only. Select a writable
 * partner to continue."). See #4265 ("make errors more informative").
 *
 * RA's default `onError` also dispatches `{ type: 'error' }` to reset the
 * editor's internal state machine from "saving" back to "reading". Overriding
 * `onError` drops that (the reducer is private), so the editor would hang in the
 * grayed "saving" state until a refetch. We reset it by remounting via a `key`
 * bump — the fresh instance starts in "reading" and re-reads the record, which
 * react-query has already rolled back to its server value on error.
 *
 * A caller can still override `onError` (or any other mutation option) by
 * passing its own `mutationOptions`; ours only fills the gap.
 */
export const AegleInPlaceEditor = ({ mutationOptions, ...rest }: InPlaceEditorProps) => {
  const notifyError = useRichApiErrorNotify();
  const [resetKey, setResetKey] = useState(0);
  return (
    <InPlaceEditor
      key={resetKey}
      {...rest}
      mutationOptions={{
        onError: (error: unknown) => {
          notifyError(error);
          setResetKey((k) => k + 1);
        },
        ...mutationOptions,
      }}
    />
  );
};
